import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Header from './components/Header.tsx';
import TimerTabs from './components/TimerTabs.tsx';
import TimerDisplay from './components/TimerDisplay.tsx';
import StartButton from './components/StartButton.tsx';
import UpcomingSessions from './components/UpcomingSessions.tsx';
import type { TaskSession, SessionType } from './types';
import TaskPanel from './components/TaskPanel.tsx';
import { Storage } from './utils/storage.ts';
import { uid } from './utils/id.ts';
import SettingsModal from './components/SettingsModal.tsx';
import StatsModal from './components/StatsModal.tsx';
import StatsPage from './components/StatsPage.tsx';
import MiniTimerPIP from './components/MiniTimerPIP.tsx';
import { MiniTimerProvider, useMiniTimer } from './contexts/MiniTimerContext.tsx';
import { TimerProvider, useTimer } from './contexts/TimerContext.tsx';
import { TaskContextProvider, useTask } from './contexts/TaskContext.tsx';
import { loadSettingsFromLocalStorage, saveSettingsToLocalStorage } from './utils/settingsStorage.ts';
import { addSessionToLog, pruneSessionLog } from './utils/sessionLog.ts';
import { playSessionCompleteSound } from './utils/audioUtils.ts';
import { useTheme } from './hooks/useTheme.ts';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.ts';
import { seedDemoData } from './utils/seedData.ts';
import './App.css';

declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
  }
}

// ---------------------------------------------------------------------------
// Seed demo data if ?seed-data URL param is present.
// Runs synchronously at module evaluation time so localStorage is populated
// before any React context reads it.
// ---------------------------------------------------------------------------
(function checkSeedParam() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('seed-data')) {
      const reset = params.get('seed-data') === 'reset';
      seedDemoData(reset);
      // Strip the param from the URL so a normal refresh doesn't re-seed
      const url = new URL(window.location.href);
      url.searchParams.delete('seed-data');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    }
  } catch {
    // Silently ignore — seeding is a dev/QA convenience only
  }
})();

const AppInner: React.FC<{
  setIsTaskPanelCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
}> = ({ setIsTaskPanelCollapsed, children }) => {
  const { handleStartStop, handleSkip, handleReset, timeLeft, isActive, sessionStarted } = useTimer();
  const { toggleMiniTimer } = useMiniTimer();
  const shortcuts = useMemo(() => ({
    onToggle: handleStartStop,
    onSkip: handleSkip,
    onReset: handleReset,
    onToggleTasks: () => setIsTaskPanelCollapsed(c => !c),
    onToggleMini: toggleMiniTimer,
  }), [handleStartStop, handleSkip, handleReset, setIsTaskPanelCollapsed, toggleMiniTimer]);
  useKeyboardShortcuts(shortcuts);

  useEffect(() => {
    if (isActive && sessionStarted) {
      const m = String(Math.floor(timeLeft / 60)).padStart(2, '0');
      const s = String(timeLeft % 60).padStart(2, '0');
      document.title = `${m}:${s} · KairoFocus`;
    } else {
      document.title = 'KairoFocus — Pomodoro Timer';
    }
  }, [timeLeft, isActive, sessionStarted]);

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const [settings, setSettings] = useState(() => loadSettingsFromLocalStorage());
  const [isTaskPanelCollapsed, setIsTaskPanelCollapsed] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isStatsOpen, setIsStatsOpen] = useState(false);
  const [view, setView] = useState<'timer' | 'stats'>(
    () => window.location.pathname === '/stats' ? 'stats' : 'timer'
  );

  const navigateTo = useCallback((path: '/' | '/stats') => {
    window.history.pushState({}, '', path);
    setView(path === '/stats' ? 'stats' : 'timer');
  }, []);

  useEffect(() => {
    const onPop = () => setView(window.location.pathname === '/stats' ? 'stats' : 'timer');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const { isMiniTimerVisible, hideMiniTimer } = useMiniTimer();
  const { tasks, currentTaskId, addTaskTime } = useTask();

  const [taskStartTime, setTaskStartTime] = useState<number | null>(null);
  const [currentTaskElapsedSeconds, setCurrentTaskElapsedSeconds] = useState(0);

  // Refs so the currentTaskId-change effect and beforeunload handler always see
  // fresh values without stale closures.
  const taskStartTimeRef = useRef<number | null>(null);
  const currentTaskIdRef = useRef<string | null>(currentTaskId);
  const prevTaskIdRef = useRef<string | null>(currentTaskId);
  useEffect(() => { pruneSessionLog(); }, []);
  useEffect(() => { taskStartTimeRef.current = taskStartTime; }, [taskStartTime]);
  useEffect(() => { currentTaskIdRef.current = currentTaskId; }, [currentTaskId]);

  // Apply theme, density, palette, and background preset to <html> element
  useTheme(settings);

  /**
   * Writes a TaskSession entry to kf-task-sessions AND bumps totalTimeSpent on
   * the task. Called on task-switch, session complete/interrupt, pause, and
   * beforeunload so that no elapsed time is ever silently dropped.
   */
  const flushTaskSession = useCallback((
    taskId: string,
    startTime: number,
    endTime: number
  ) => {
    const duration = Math.floor((endTime - startTime) / 1000);
    if (duration <= 0) return;
    const entry: TaskSession = {
      sessionId: uid(),
      taskId,
      startTime,
      endTime,
      duration,
      date: new Date(startTime).toISOString().split('T')[0],
    };
    const existing = Storage.read<TaskSession[]>('kf-task-sessions', []);
    Storage.write('kf-task-sessions', [...existing, entry]);
    addTaskTime(taskId, duration);
  }, [addTaskTime]);

  // When the user switches task mid-session, flush elapsed time to the old task
  // and restart the clock for the new one.
  useEffect(() => {
    const prevId = prevTaskIdRef.current;
    prevTaskIdRef.current = currentTaskId;
    if (prevId === currentTaskId) return;

    const start = taskStartTimeRef.current;
    if (start !== null && prevId) {
      flushTaskSession(prevId, start, Date.now());
    }

    setCurrentTaskElapsedSeconds(0);
    // Keep tracking if the timer is running and a new task is selected
    setTaskStartTime(start !== null && currentTaskId ? Date.now() : null);
  }, [currentTaskId, flushTaskSession]);

  // Flush the active task's elapsed time when the browser tab is closed or
  // refreshed so that in-progress time is never silently lost.
  useEffect(() => {
    const handleBeforeUnload = () => {
      const taskId = currentTaskIdRef.current;
      const startTime = taskStartTimeRef.current;
      if (!taskId || startTime === null) return;
      flushTaskSession(taskId, startTime, Date.now());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushTaskSession]);

  const getCurrentTaskName = useCallback(() => {
    if (!currentTaskId) return null;
    return tasks.find(t => t.id === currentTaskId)?.text ?? null;
  }, [currentTaskId, tasks]);

  const handleSessionComplete = useCallback((
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    nextSessionType: SessionType
  ) => {
    if (sessionType === 'pomodoro' && currentTaskId && taskStartTime) {
      flushTaskSession(currentTaskId, taskStartTime, endTime);
      setCurrentTaskElapsedSeconds(0);
      setTaskStartTime(null);
    }

    addSessionToLog(sessionId, sessionType, startTime, endTime, plannedDuration, plannedDuration, 'completed', currentTaskId ?? undefined);
    playSessionCompleteSound(sessionType, settings);

    if ('Notification' in window && Notification.permission === 'granted') {
      const sessionNames: Record<SessionType, string> = {
        pomodoro: 'Work session',
        shortBreak: 'Short break',
        longBreak: 'Long break',
      };
      const nextNames: Record<SessionType, string> = {
        pomodoro: 'Work session',
        shortBreak: `Short break (${settings.shortBreakDuration} min)`,
        longBreak: `Long break (${settings.longBreakDuration} min)`,
      };
      const n = new Notification(`${sessionNames[sessionType]} complete!`, {
        body: `${nextNames[nextSessionType]} next`,
        icon: '/favicon.ico',
        tag: 'timer-complete',
        requireInteraction: true,
      });
      setTimeout(() => n.close(), 5000);
    }
  }, [currentTaskId, taskStartTime, flushTaskSession, settings]);

  const handleSessionInterrupt = useCallback((
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    actualDuration: number
  ) => {
    if (sessionType === 'pomodoro' && currentTaskId && taskStartTime) {
      flushTaskSession(currentTaskId, taskStartTime, endTime);
    }
    addSessionToLog(sessionId, sessionType, startTime, endTime, plannedDuration, actualDuration, 'interrupted', currentTaskId ?? undefined);
  }, [currentTaskId, taskStartTime, flushTaskSession]);

  const handlePause = useCallback(() => {
    if (currentTaskId && taskStartTime) {
      flushTaskSession(currentTaskId, taskStartTime, Date.now());
    }
    setCurrentTaskElapsedSeconds(0);
    setTaskStartTime(null);
  }, [currentTaskId, taskStartTime, flushTaskSession]);

  const handleSessionSkip = useCallback((
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    actualDuration: number
  ) => {
    addSessionToLog(sessionId, sessionType, startTime, endTime, plannedDuration, actualDuration, 'skipped', currentTaskId ?? undefined);
    setCurrentTaskElapsedSeconds(0);
    setTaskStartTime(null);
  }, [currentTaskId]);

  const handleResume = useCallback((tab: string) => {
    if (currentTaskId && tab === 'pomodoro') {
      setTaskStartTime(Date.now());
    }
  }, [currentTaskId]);

  const handleTimerReset = useCallback(() => {
    setCurrentTaskElapsedSeconds(0);
    setTaskStartTime(null);
  }, []);

  useEffect(() => {
    if (!taskStartTime || !currentTaskId) return;
    const interval = setInterval(() => {
      setCurrentTaskElapsedSeconds(Math.floor((Date.now() - taskStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [taskStartTime, currentTaskId]);

  const handleSettingsChange = (newSettings: any) => {
    setSettings(newSettings);
    saveSettingsToLocalStorage(newSettings);
  };

  // TimerProvider and MiniTimerPIP are always mounted regardless of view so that:
  // - the timer keeps running when the user navigates to Stats
  // - the Mini Timer overlay stays visible across view transitions
  return (
    <TimerProvider
      settings={settings}
      currentTaskId={currentTaskId}
      onSessionComplete={handleSessionComplete}
      onSessionInterrupt={handleSessionInterrupt}
      onSessionSkip={handleSessionSkip}
      onPause={handlePause}
      onResume={handleResume}
      onReset={handleTimerReset}
    >
      <AppInner setIsTaskPanelCollapsed={setIsTaskPanelCollapsed}>
        {view === 'stats' ? (
          <StatsPage onBack={() => navigateTo('/')} />
        ) : (
          <div className="app">
            <Header onSettingsClick={() => setIsSettingsOpen(true)} onStatsClick={() => navigateTo('/stats')} />
            <div className="app-content">
              <div className={`main-content ${isTaskPanelCollapsed ? 'panel-collapsed' : 'panel-expanded'}`}>
                <TimerTabs />
                <TimerDisplay currentTaskName={getCurrentTaskName()} />
                <StartButton />
                <UpcomingSessions settings={settings} />
              </div>
              <TaskPanel
                isCollapsed={isTaskPanelCollapsed}
                onToggle={() => setIsTaskPanelCollapsed(c => !c)}
                currentTaskElapsedSeconds={currentTaskElapsedSeconds}
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            </div>
            <SettingsModal
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              settings={settings}
              onSettingsChange={handleSettingsChange}
            />
            <StatsModal isOpen={isStatsOpen} onClose={() => setIsStatsOpen(false)} />
          </div>
        )}
        <MiniTimerPIP
          isVisible={isMiniTimerVisible}
          onClose={hideMiniTimer}
          currentTaskName={getCurrentTaskName()}
        />
      </AppInner>
    </TimerProvider>
  );
};

const App: React.FC = () => {
  return (
    <MiniTimerProvider>
      <TaskContextProvider>
        <AppContent />
      </TaskContextProvider>
    </MiniTimerProvider>
  );
};

export default App;
